<template>
    <v-container v-if="isAuthorized">
        <div class="ai-hero">
            <div class="d-flex align-center">
                <v-icon x-large class="mr-4">mdi-creation</v-icon>
                <div>
                    <div class="ai-hero__title">AI Settings</div>
                    <div class="ai-hero__subtitle">Power up MessageIQ with AI-generated content, on your own key</div>
                </div>
            </div>
        </div>

        <v-row>
            <v-col cols="12" md="8">
                <p class="text-overline grey--text mb-2">Choose a provider</p>
                <v-row class="ai-provider-grid mb-2">
                    <v-col cols="6" sm="3" v-for="p in providerOptions" :key="p.value">
                        <div
                            class="ai-select-card-wrap"
                            :class="{ 'ai-select-card-wrap--selected': form.provider === p.value }"
                            @click="selectProvider(p.value)">
                            <v-card flat class="ai-provider-card text-center py-4">
                                <v-icon
                                    v-if="isConnected(p.value)"
                                    class="ai-provider-card__badge"
                                    color="success">
                                    mdi-check-circle
                                </v-icon>
                                <div class="ai-provider-card__logo">
                                    <svg v-if="p.iconPath" viewBox="0 0 24 24" width="32" height="32">
                                        <path :d="p.iconPath" :fill="p.color"></path>
                                    </svg>
                                    <v-icon v-else :color="p.color" size="32">{{ p.mdiIcon }}</v-icon>
                                </div>
                                <div class="ai-provider-card__name mt-2">{{ p.text }}</div>
                                <div
                                    class="ai-provider-card__status success--text"
                                    :style="{ visibility: isConnected(p.value) ? 'visible' : 'hidden' }">
                                    Connected
                                </div>
                            </v-card>
                        </div>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <v-row v-if="showProviderForm">
            <v-col cols="12" md="6">
                <v-card outlined>
                    <v-card-title class="text-body-1 d-flex align-center">
                        Connect {{ selectedProviderLabel }}
                        <v-chip v-if="isConnected(form.provider)" x-small color="success" text-color="white" class="ml-2">
                            <v-icon left x-small>mdi-check</v-icon>Connected
                        </v-chip>
                        <v-spacer></v-spacer>
                        <v-btn text x-small color="primary" @click="showInstructions = !showInstructions">
                            <v-icon left x-small>{{ showInstructions ? 'mdi-chevron-up' : 'mdi-help-circle-outline' }}</v-icon>
                            {{ showInstructions ? 'Hide' : 'How do I get an API key?' }}
                        </v-btn>
                    </v-card-title>

                    <v-card-text v-if="showInstructions" class="pt-0">
                        <div class="callout calm">
                            <p class="font-weight-medium mb-2">Getting an API key from {{ currentInstructions.name }}</p>
                            <ol class="pl-4 mb-2">
                                <li v-for="(step, index) in currentInstructions.steps" :key="index" class="mb-1">{{ step }}</li>
                            </ol>
                            <v-btn
                                :href="currentInstructions.url"
                                target="_blank"
                                rel="noopener"
                                x-small
                                outlined
                                color="primary">
                                Open {{ currentInstructions.name }}
                                <v-icon right x-small>mdi-open-in-new</v-icon>
                            </v-btn>
                        </div>
                    </v-card-text>

                    <v-card-text>
                        <v-form v-model="valid" ref="aiSettingsForm">
                            <v-select
                                v-if="form.provider !== 'AzureOpenAI'"
                                v-model="form.model"
                                :items="modelOptions"
                                label="Model"
                                outlined
                                dense
                                :rules="requiredRule"
                            ></v-select>
                            <v-text-field
                                v-else
                                v-model="form.model"
                                label="Deployment Name"
                                outlined
                                dense
                                hint="The deployment name you created for this model in your Azure OpenAI resource - not the model name itself."
                                persistent-hint
                                :rules="requiredRule"
                            ></v-text-field>

                            <v-text-field
                                v-model="form.apiKey"
                                :label="currentAiSettings.isConfigured ? 'API Key (leave blank to keep current key)' : 'API Key'"
                                outlined
                                dense
                                type="password"
                                autocomplete="new-password"
                                :rules="currentAiSettings.isConfigured ? [] : requiredRule"
                            ></v-text-field>

                            <v-text-field
                                v-if="form.provider === 'AzureOpenAI'"
                                v-model="form.endpoint"
                                label="Azure Resource Endpoint"
                                placeholder="https://your-resource.openai.azure.com"
                                outlined
                                dense
                                hint="Found in the Azure Portal under your Azure OpenAI resource's Keys and Endpoint page."
                                persistent-hint
                                :rules="requiredRule"
                            ></v-text-field>

                            <v-text-field
                                v-if="form.provider === 'Anthropic'"
                                v-model="form.workspaceId"
                                label="Workspace ID (optional)"
                                outlined
                                dense
                                hint="Only needed if your Anthropic API key isn't scoped to a single workspace. If you see a 'workspace-id is required' error, either paste your Workspace ID here (Anthropic Console → Settings → Workspaces), or create a new key in the Console scoped to one workspace."
                                persistent-hint
                            ></v-text-field>

                            <v-text-field
                                v-model.number="form.monthlyBudgetUsd"
                                label="Monthly Budget (USD)"
                                type="number"
                                min="0"
                                step="0.01"
                                prefix="$"
                                outlined
                                dense
                                hint="Generation requests are rejected once this account's estimated spend reaches this amount for the period."
                                persistent-hint
                                :rules="requiredRule"
                            ></v-text-field>

                            <v-switch
                                v-model="form.includeContactFields"
                                label="Include contact/company fields when generating content"
                                inset
                            ></v-switch>

                            <v-switch
                                v-model="form.includeMessageHistory"
                                label="Include recent message history when generating content"
                                inset
                            ></v-switch>

                            <v-switch
                                v-model="form.isActive"
                                label="AI content generation enabled for this account"
                                inset
                            ></v-switch>

                            <v-btn class="ai-gradient-bg white--text" depressed @click="save" :disabled="!valid">
                                <v-icon left small>mdi-content-save</v-icon>Save
                            </v-btn>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card flat>
                    <v-card-text class="pa-0">
                        <v-row class="d-flex align-center mb-1">
                            <v-col md="6" cols="12" class="d-flex align-center">
                                <h1>Usage Report</h1>
                            </v-col>
                            <v-col md="3" cols="6">
                                <v-text-field
                                    v-model="usageFrom"
                                    label="From"
                                    type="date"
                                    dense
                                    hide-details
                                    @change="loadUsageReport"
                                ></v-text-field>
                            </v-col>
                            <v-col md="3" cols="6">
                                <v-text-field
                                    v-model="usageTo"
                                    label="To"
                                    type="date"
                                    dense
                                    hide-details
                                    @change="loadUsageReport"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row class="mb-3">
                            <v-col cols="12" sm="4">
                                <div class="ai-stat-tile">
                                    <div class="ai-stat-tile__label">Input Tokens</div>
                                    <div class="ai-stat-tile__value ai-gradient-text">{{ currentAiUsageReport.totalInputTokens.toLocaleString() }}</div>
                                </div>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <div class="ai-stat-tile">
                                    <div class="ai-stat-tile__label">Output Tokens</div>
                                    <div class="ai-stat-tile__value ai-gradient-text">{{ currentAiUsageReport.totalOutputTokens.toLocaleString() }}</div>
                                </div>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <div class="ai-stat-tile">
                                    <div class="ai-stat-tile__label">Estimated Cost</div>
                                    <div class="ai-stat-tile__value ai-gradient-text">${{ currentAiUsageReport.totalEstimatedCostUsd.toFixed(4) }}</div>
                                </div>
                            </v-col>
                        </v-row>

                        <v-data-table
                            :headers="usageHeaders"
                            :items="currentAiUsageReport.items"
                            :items-per-page="30"
                            dense
                            class="elevation-1"
                            :footer-props="{
                                itemsPerPageOptions: [15, 30, 50, -1]
                            }"
                        >
                            <template v-slot:item.dateCreated="{ item }">
                                {{ formatDate(item.dateCreated) }}
                            </template>
                            <template v-slot:item.estimatedCostUsd="{ item }">
                                ${{ item.estimatedCostUsd.toFixed(4) }}
                            </template>
                            <template v-slot:item.wasSuccessful="{ item }">
                                <v-icon v-if="item.wasSuccessful" color="success" small>mdi-check-circle</v-icon>
                                <v-icon v-else color="error" small>mdi-close-circle</v-icon>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
    <v-container v-else>
        <v-alert type="error" outlined>
            You don't have permission to view AI Settings. This page is limited to Super Admins and Account Admins.
        </v-alert>
    </v-container>
</template>
<script>
import { mapGetters } from 'vuex';
export default {
    computed: {
        ...mapGetters({
            currentCompany: 'company/getCurrentCompany',
            userRole: 'getUserRole'
        }),
        isAuthorized(){
            // TEMPORARY: AI Settings soft-launch allowlist, on top of the role check - remove the
            // company id check once ready for general availability. Kept here too (not just the
            // Settings.vue nav tab) so navigating straight to /settings/ai is also blocked.
            return (this.userRole === 'SuperAdmin' || this.userRole === 'AccountAdmin')
                && this.currentCompany.id === '158b7073-5e61-4df7-8585-ea40298231f8';
        },
        currentAiSettings(){
            return this.$store.state.ai.currentAiSettings;
        },
        currentAiUsageReport(){
            return this.$store.state.ai.currentAiUsageReport;
        },
        modelOptions(){
            return this.modelsByProvider[this.form.provider] || [];
        },
        selectedProviderLabel(){
            let match = this.providerOptions.find(p => p.value === this.form.provider);
            return match ? match.text : this.form.provider;
        },
        currentInstructions(){
            return this.providerInstructions[this.form.provider] || { name: this.selectedProviderLabel, url: '#', steps: [] };
        }
    },
    watch: {
        'form.provider'(newProvider, oldProvider){
            if(newProvider === oldProvider){
                return;
            }
            // Azure has no fixed model list (deployment names are account-specific free text);
            // every other provider switches to its first curated model.
            this.form.model = newProvider === 'AzureOpenAI' ? '' : (this.modelsByProvider[newProvider] || [])[0] || '';
        }
    },
    data () {
        return {
            valid: false,
            showProviderForm: false,
            showInstructions: false,
            // Verified against each provider's own current docs - update if a provider changes
            // where this lives in their console.
            providerInstructions: {
                Anthropic: {
                    name: 'Anthropic Console',
                    url: 'https://platform.claude.com/settings/keys',
                    steps: [
                        'Sign in at platform.claude.com (create an account if you don\'t have one).',
                        'Go to Settings → API Keys.',
                        'Click "Create Key" and name it (e.g. "MessageIQ").',
                        'When prompted, scope the key to a single workspace - this avoids needing to also enter a Workspace ID below.',
                        'Copy the key (starts with sk-ant-) and paste it below - Anthropic only shows it once.',
                        'If you didn\'t scope the key to one workspace (or you\'re not sure), find your Workspace ID under Settings → Workspaces - it\'s shown in the ID column for each workspace listed there.',
                        'Paste that Workspace ID into the "Workspace ID" field below. If you use the Default Workspace, it isn\'t listed on that page - instead, make any request with the key and copy the anthropic-workspace-id value from the response headers.'
                    ]
                },
                OpenAI: {
                    name: 'OpenAI Platform',
                    url: 'https://platform.openai.com/api-keys',
                    steps: [
                        'Sign in at platform.openai.com (create an account if you don\'t have one).',
                        'Under Settings → Billing, add a payment method if you haven\'t already - the API requires billing to be set up before it will generate usable keys.',
                        'Go to platform.openai.com/api-keys.',
                        'Click "Create new secret key" and name it (e.g. "MessageIQ").',
                        'Copy the key (starts with sk-) and paste it below - OpenAI only shows it once.'
                    ]
                },
                Google: {
                    name: 'Google AI Studio',
                    url: 'https://aistudio.google.com/apikey',
                    steps: [
                        'Sign in at aistudio.google.com/apikey with a Google account.',
                        'Accept the Terms of Service if this is your first time - a Google Cloud project is created for you automatically.',
                        'Click "Create API key".',
                        'Choose an existing Google Cloud project, or let Google create one for you.',
                        'Copy the generated key and paste it below.'
                    ]
                },
                AzureOpenAI: {
                    name: 'Azure Portal',
                    url: 'https://portal.azure.com/#create/Microsoft.CognitiveServicesOpenAI',
                    steps: [
                        'In the Azure Portal, select "Create a resource", search for "Azure OpenAI", and create the resource (requires an Azure subscription and Azure OpenAI access approval on the subscription).',
                        'Once it\'s created, open the resource and go to "Keys and Endpoint" to copy a key and the Endpoint URL - paste both below.',
                        'In Microsoft Foundry (ai.azure.com), open your resource, go to Deployments, and select "+ Deploy model" to deploy the model you want to use. Give the deployment a name.',
                        'Enter that deployment name below in "Deployment Name" - Azure calls the model by its deployment name, not the model name itself.'
                    ]
                }
            },
            requiredRule: [v => (v !== null && v !== undefined && v !== '') || 'Required'],
            // SVG path data for Anthropic/OpenAI/Google is the verified mark from Simple Icons
            // (simpleicons.org, CC0) at their documented brand color. Azure OpenAI uses the MDI
            // "microsoft-azure" glyph already loaded by this app rather than an svg path, since a
            // reliable current brand-icon source for it wasn't available.
            providerOptions: [
                {
                    text: 'Anthropic (Claude)',
                    value: 'Anthropic',
                    color: '#191919',
                    iconPath: 'M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z'
                },
                {
                    text: 'OpenAI',
                    value: 'OpenAI',
                    color: '#412991',
                    iconPath: 'M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z'
                },
                {
                    text: 'Google (Gemini)',
                    value: 'Google',
                    color: '#4285F4',
                    iconPath: 'M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z'
                },
                {
                    text: 'Azure OpenAI',
                    value: 'AzureOpenAI',
                    color: '#0078D4',
                    mdiIcon: 'mdi-microsoft-azure'
                }
            ],
            modelsByProvider: {
                Anthropic: ['claude-sonnet-5', 'claude-opus-5', 'claude-haiku-4-5'],
                OpenAI: ['gpt-5.6-terra', 'gpt-5.6-sol', 'gpt-5.6-luna'],
                Google: ['gemini-2.5-flash', 'gemini-2.5-pro']
            },
            form: {
                provider: 'Anthropic',
                model: 'claude-sonnet-5',
                apiKey: '',
                workspaceId: '',
                endpoint: '',
                monthlyBudgetUsd: 0,
                includeContactFields: false,
                includeMessageHistory: false,
                isActive: true
            },
            usageFrom: null,
            usageTo: null,
            usageHeaders: [
                { text: 'Date', value: 'dateCreated' },
                { text: 'User', value: 'userDisplayName' },
                { text: 'Provider', value: 'provider' },
                { text: 'Model', value: 'model' },
                { text: 'Feature', value: 'feature' },
                { text: 'Input Tokens', value: 'inputTokens' },
                { text: 'Output Tokens', value: 'outputTokens' },
                { text: 'Cost', value: 'estimatedCostUsd' },
                { text: 'Success', value: 'wasSuccessful' }
            ]
        }
    },
    async mounted () {
        this.$auth.auth0ClientPromise.then(async () => {
            if(Object.keys(this.currentCompany).length === 0){
                await this.$store.dispatch('getUserCompany');
            }

            if(!this.isAuthorized){
                return;
            }

            await this.$store.dispatch('ai/getAiSettings', this.currentCompany.id);
            this.applySettingsToForm();

            await this.loadUsageReport();
        });
    },
    methods: {
        isConnected(providerValue){
            return this.currentAiSettings.isConfigured && this.currentAiSettings.provider === providerValue;
        },
        selectProvider(providerValue){
            this.form.provider = providerValue;
            this.showProviderForm = true;
            // Surface setup instructions by default when picking a provider that isn't connected
            // yet - once it's connected there's nothing left to set up, so keep it tucked away.
            this.showInstructions = !this.isConnected(providerValue);
        },
        applySettingsToForm(){
            if(!this.currentAiSettings.isConfigured){
                return;
            }

            this.form.provider = this.currentAiSettings.provider;
            this.form.model = this.currentAiSettings.model;
            this.form.workspaceId = this.currentAiSettings.workspaceId || '';
            this.form.endpoint = this.currentAiSettings.endpoint || '';
            this.form.monthlyBudgetUsd = this.currentAiSettings.monthlyBudgetUsd;
            this.form.includeContactFields = this.currentAiSettings.includeContactFields;
            this.form.includeMessageHistory = this.currentAiSettings.includeMessageHistory;
            this.form.isActive = this.currentAiSettings.isActive;
            this.form.apiKey = '';
            // Already connected - show that provider's settings right away instead of making
            // the admin click the logo again just to see what's configured.
            this.showProviderForm = true;
        },
        async save(){
            if(!this.$refs.aiSettingsForm.validate()){
                return;
            }

            let payload = {
                companyId: this.currentCompany.id,
                provider: this.form.provider,
                model: this.form.model,
                apiKey: this.form.apiKey,
                workspaceId: this.form.workspaceId,
                endpoint: this.form.endpoint,
                monthlyBudgetUsd: this.form.monthlyBudgetUsd,
                includeContactFields: this.form.includeContactFields,
                includeMessageHistory: this.form.includeMessageHistory,
                isActive: this.form.isActive
            };

            let success = await this.$store.dispatch('ai/updateAiSettings', payload);

            if(success){
                this.applySettingsToForm();
                this.$swal({
                    icon: 'success',
                    title: 'AI Settings Updated',
                    text: 'AI settings successfully updated.',
                    toast: true,
                    timer: 3000,
                    position: 'top-end'
                });
            }else{
                this.$swal({
                    icon: 'error',
                    title: 'Error Updating AI Settings',
                    text: 'An error occurred while updating AI settings. Please try again later.',
                    toast: true,
                    timer: 3000,
                    position: 'top-end'
                });
            }
        },
        async loadUsageReport(){
            await this.$store.dispatch('ai/getAiUsageReport', {
                companyId: this.currentCompany.id,
                from: this.usageFrom,
                to: this.usageTo
            });
        },
        formatDate(dateString){
            let d = new Date(dateString);
            return d.toLocaleString();
        }
    }
}
</script>
